import Konva from "konva";
import { ContractFunctionExecutionError } from "viem";
import { LiskClient } from "../clients/lisk_client";
import { Stencil } from "../stencil/types";
import { debounce } from "../util/helpers";
import { Canvas } from "./canvas";
import { Confirmation } from "./confirmation";
import { DeferredRender } from "./deferred_render";
import { Highlightable } from "./highlightable";
import { Coords, Pixel } from "./types";
import { Zoomies } from "./zoomies";

export class DrawHandler implements DeferredRender {
    private static readonly DRAW_CHANGES_KEY = "_dc";

    private readonly canvas: Canvas;
    private readonly confirmation: Confirmation;
    private readonly highlightable: Highlightable;
    private readonly zoomies: Zoomies;
    private readonly layer: Konva.Layer;
    private readonly group: Konva.Group;
    private freeDraw: boolean = false;
    private mouseDown: boolean = false;
    private pixelUndoHistory: Pixel[][] = [];
    private pixelRedoHistory: Pixel[][] = [];
    private pixelChangeByCoords: Record<string, Pixel[]> = {};
    private onCommitListeners: (() => void)[] = [];
    private onCancelListeners: (() => void)[] = [];
    private onConfirmCancelListeners: (() => void)[] = [];
    private onRefreshListeners: (() => void)[] = [];
    private onInfoListeners: ((message: string) => void)[] = [];
    private onSuccessListeners: ((message: string) => void)[] = [];
    private onErrorListeners: ((message: string) => void)[] = [];
    private drawColour: string|null = null;
    private selectedCoords: Coords|null = null;
    private selectedStencil: Stencil|null = null;
    private failures: number = 0;
    private saveChangesDebounce: () => void = debounce(() => this.saveChanges(), 2000);
    private mouseUpHandler: (e: MouseEvent) => void = (e) => this.onMouseUp(e);
    private keydownHandler: (e: KeyboardEvent) => void = (e) => this.onKeyDown(e);

    public constructor(
        canvas: Canvas,
        confirmation: Confirmation,
        highlightable: Highlightable,
        zoomies: Zoomies,
        layer: Konva.Layer
    ) {
        this.canvas = canvas;
        this.confirmation = confirmation;
        this.highlightable = highlightable;
        this.zoomies = zoomies;
        this.layer = layer;
        this.group = new Konva.Group();
    }

    public load(): void {
        this.layer.add(this.group);
        this.layer.on("mousedown", (e) => this.onMouseDown(e));
        this.layer.on("touchstart", () => this.onTouchStart());
        this.layer.on("touchend", () => this.onTouchEnd());
        window.addEventListener("mouseup", this.mouseUpHandler);
        document.addEventListener("keydown", this.keydownHandler);
        this.confirmation.registerOnAcceptListener(() => this.onAccept());
        this.confirmation.registerOnCancelListener(() => this.onCancel());
        this.highlightable.registerOnSelectListener((coords: Coords) => this.onSelect(coords));
        this.highlightable.registerOnDeselectListener(() => this.onDeselect());
        this.highlightable.registerOnHighlightListener((coords: Coords) => this.onHighlight(coords));
        this.highlightable.registerOnClickListener((coords: Coords) => this.onClick(coords));
        this.loadChanges();
    }

    public unload(): void {
        this.group.remove();
        this.layer.off("mousedown touchstart touchend");
        window.removeEventListener("mouseup", this.mouseUpHandler);
        this.onCommitListeners = [];
        this.onCancelListeners = [];
        this.onConfirmCancelListeners = [];
        this.onRefreshListeners = [];
        this.onInfoListeners = [];
        this.onSuccessListeners = [];
        this.onErrorListeners = [];
    }

    public fitIntoStage(stage: Konva.Stage) {
        // Do nothing
    }

    public registerOnCommitListener(callback: () => void): void {
        this.onCommitListeners.push(callback);
    }

    public registerOnCancelListener(callback: () => void): void {
        this.onCancelListeners.push(callback);
    }

    public registerOnConfirmCancelListener(callback: () => void): void {
        this.onConfirmCancelListeners.push(callback);
    }

    public registerOnRefreshListener(callback: () => void): void {
        this.onRefreshListeners.push(callback);
    }

    public registerOnInfoListener(callback: (message: string) => void): void {
        this.onInfoListeners.push(callback);
    }

    public registerOnSuccessListener(callback: (message: string) => void): void {
        this.onSuccessListeners.push(callback);
    }

    public registerOnErrorListener(callback: (message: string) => void): void {
        this.onErrorListeners.push(callback);
    }

    public enableFreeDraw(): void {
        this.freeDraw = true;
    }

    public disableFreeDraw(): void {
        this.freeDraw = false;
    }

    public freeDrawEnabled(): boolean {
        return this.freeDraw;
    }

    public setDrawColour(colour: string|null): void {
        if (colour !== null && this.selectedCoords !== null) {
            this.overlayPixel(this.selectedCoords.x, this.selectedCoords.y, colour);
        }

        this.drawColour = colour;
        this.highlightable.updateCanSelect(colour === null);
    }

    public getOverlayColour(coords: Coords): string|null {
        const serialised = this.serialiseCoords(coords.x, coords.y);

        if (!(serialised in this.pixelChangeByCoords) || this.pixelChangeByCoords[serialised].length === 0) {
            return null;
        }

        return this.pixelChangeByCoords[serialised][this.pixelChangeByCoords[serialised].length - 1].colour.toLowerCase();
    }

    public undo(): void {
        const lastChanges = this.pixelUndoHistory.pop();

        if (!lastChanges) {
            return;
        }

        const overlayXCoords = [];
        const overlayYCoords = [];
        const overlayColours = [];
        const clearXCoords = [];
        const clearYCoords = [];

        for (const lastChange of lastChanges) {
            const coords = this.serialiseCoords(lastChange.x, lastChange.y);
            const mapped = this.pixelChangeByCoords[coords];

            if (mapped.length !== 0 && mapped[mapped.length - 1] === lastChange) {
                mapped.pop();
            }

            if (mapped.length !== 0) {
                const lastPixel = mapped[mapped.length - 1];
                overlayXCoords.push(lastPixel.x);
                overlayYCoords.push(lastPixel.y);
                overlayColours.push(lastPixel.colour);
            } else {
                clearXCoords.push(lastChange.x);
                clearYCoords.push(lastChange.y);
            }
        }

        this.canvas.overlayAndClear(
            overlayXCoords,
            overlayYCoords,
            overlayColours,
            clearXCoords,
            clearYCoords,
        );

        if (this.pixelUndoHistory.length === 0) {
            this.confirmation.hide();
        }

        this.pixelRedoHistory.push(lastChanges);
    }

    public redo(): void {
        const lastChanges = this.pixelRedoHistory.pop();

        if (!lastChanges) {
            return;
        }

        const xCoords = [];
        const yCoords = [];
        const colours = [];

        for (const pixel of lastChanges) {
            xCoords.push(pixel.x);
            yCoords.push(pixel.y);
            colours.push(pixel.colour);
        }

        this.pixelUndoHistory.push([]);
        this.overlayPixels(xCoords, yCoords, colours);
    }

    public draw(pixels: Pixel[]): void {
        const xCoords = [];
        const yCoords = [];
        const colours = [];

        for (const pixel of pixels) {
            if (pixel.x >= 0 && pixel.x < this.canvas.getProperties().width &&
                pixel.y >= 0 && pixel.y < this.canvas.getProperties().height &&
                this.canvas.getProperties().colourPalette.includes(pixel.colour.toLowerCase())
            ) {
                xCoords.push(pixel.x);
                yCoords.push(pixel.y);
                colours.push(pixel.colour);
            }
        }

        this.pixelUndoHistory.push([]);
        this.overlayPixels(xCoords, yCoords, colours);
    }

    public drawStencil(stencil: Stencil): void {
        this.cancel();
        this.selectedStencil = stencil;
    }

    public cancel(): void {
        this.clearCanvas();
        this.confirmation.hide();
        this.onCancelListeners.forEach((listener) => listener());
        this.removeChanges();
    }

    private clearCanvas(): void {
        // Clear pixel changes before resetting canvas so that original pixel cache isn't redundantly modified
        this.pixelUndoHistory = [];
        this.pixelChangeByCoords = {};
        // Reset canvas
        this.canvas.clearOverlay();
    }

    private onMouseDown(e: Konva.KonvaEventObject<MouseEvent>): void {
        if (!this.isButton(e.evt, 0)) {
            return;
        }

        this.onTouchStart();
    }

    private onMouseUp(e: MouseEvent): void {
        if (!this.isButton(e, 0)) {
            return;
        }

        this.onTouchEnd();
    }

    private onTouchStart(): void {
        this.mouseDown = true;

        if (this.selectedStencil !== null) {
            this.selectedStencil = null;
            return;
        }

        if (this.pixelUndoHistory.length === 0 || this.pixelUndoHistory[this.pixelUndoHistory.length - 1].length > 0) {
            this.pixelUndoHistory.push([]);
        }

        if (this.drawColour !== null && !this.highlightable.canHighlight()) {
            const coords = this.highlightable.getPointerCoords();

            if (coords !== null) {
                this.zoomies.zoomToCoords(coords, false);
            } else {
                this.onErrorListeners.forEach((listener) => listener("Zoom in further to draw"));
            }

            return;
        }

        const coords = this.highlightable.getHighlightedCoords();

        if (coords !== null) {
            this.onHighlight(coords);
        }
    }

    private onTouchEnd(): void {
        this.mouseDown = false;
    }

    private onKeyDown(e: KeyboardEvent): void {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
            if (e.shiftKey) {
                e.preventDefault();
                this.redo();
            } else {
                e.preventDefault();
                this.undo();
            }
        }
    }

    private async onAccept(): Promise<void> {
        const properties = this.canvas.getProperties();
        const xCoords: number[] = [];
        const yCoords: number[] = [];
        const colours: string[] = [];
        const serialisedCoords: number[] = [];
        const serialisedColours: number[] = [];

        for (const key in this.pixelChangeByCoords) {
            if (this.pixelChangeByCoords[key].length === 0) {
                continue;
            }

            // Only apply last drawn pixel at coordinate
            const last = this.pixelChangeByCoords[key][this.pixelChangeByCoords[key].length - 1];
            const x = last.x;
            const y = last.y;
            const colour = last.colour.toLowerCase();
            xCoords.push(x);
            yCoords.push(y);
            colours.push(colour);
            serialisedCoords.push((y * properties.width) + x);
            serialisedColours.push(properties.colourPalette.findIndex((palette) => palette.toLowerCase() === colour));
        }

        try {
            this.onCancelListeners.forEach((listener) => listener());
            this.notifyInfo("Submitting transaction ...");
            await LiskClient.getInstance().draw(properties.canvasId, serialisedCoords, serialisedColours);
            this.failures = 0;
        } catch (error) {
            if (error instanceof ContractFunctionExecutionError) {
                if (error.message.includes("User rejected")) {
                    this.onErrorListeners.forEach((listener) => listener("Transaction rejected"));
                    return;
                }
            }

            console.log(error);

            if (++this.failures >= 3) {
                this.saveChanges();
                this.onRefreshListeners.forEach((listener) => listener());
                this.failures = 0;
                return;
            }

            this.onErrorListeners.forEach((listener) => listener("Transaction failed"));
            return;
        }

        this.canvas.updateCanvas(xCoords, yCoords, colours);
        this.canvas.clearOverlay();

        this.pixelUndoHistory = [];
        this.pixelChangeByCoords = {};
        this.removeChanges();
        this.confirmation.hide();
        this.onCommitListeners.forEach((listener) => listener());
        this.notifySuccess("Transaction submitted!");
    }

    private onCancel(): void {
        this.selectedStencil = null;
        this.onConfirmCancelListeners.forEach((listener) => listener());
    }

    private onSelect(coords: Coords): void {
        if (this.drawColour !== null) {
            this.overlayPixel(coords.x, coords.y, this.drawColour);
        }

        this.selectedCoords = coords;
    }

    private onDeselect(): void {
        this.selectedCoords = null;
    }

    private onHighlight(coords: Coords): void {
        if (this.selectedStencil !== null) {
            this.clearCanvas();
            const pixels = this.selectedStencil.pixels.map((pixel) => ({
                x: pixel.x + coords.x,
                y: pixel.y + coords.y,
                colour: pixel.colour,
            }));
            this.draw(pixels);
            return;
        }

        if (this.freeDraw && this.mouseDown && this.drawColour !== null) {
            this.overlayPixel(coords.x, coords.y, this.drawColour);
        }
    }

    private onClick(coords: Coords): void {
        if (this.drawColour !== null && this.highlightable.canHighlight()) {
            this.overlayPixel(coords.x, coords.y, this.drawColour);
        }
    }

    private overlayPixel(x: number, y: number, colour: string): void {
        this.pixelRedoHistory = [];
        this.overlayPixels([x], [y], [colour]);
    }

    private overlayPixels(xCoords: number[], yCoords: number[], colours: string[]): void {
        if (xCoords.length !== yCoords.length || xCoords.length !== colours.length) {
            throw new Error("Invalid pixels");
        }

        for (let i = 0; i < xCoords.length; ++i) {
            // TODO: USE QUERIED PROPERTIES AND NOT HARDCODED NUMBER
            if (Object.keys(this.pixelChangeByCoords).length >= 10000) {
                this.notifyError("Maximum pixels reached, please submit transaction");
                return;
            }

            const x = xCoords[i];
            const y = yCoords[i];
            const colour = colours[i];
            const coords = this.serialiseCoords(x, y);

            if (!(coords in this.pixelChangeByCoords)) {
                this.pixelChangeByCoords[coords] = [];
            }

            if (this.pixelChangeByCoords[coords].length !== 0) {
                const current = this.pixelChangeByCoords[coords][this.pixelChangeByCoords[coords].length - 1];

                // Discarding if already set to given colour
                if (current.colour === colour) {
                    return;
                }
            }

            const change = { x, y, colour };
            this.pixelUndoHistory[this.pixelUndoHistory.length - 1].push(change);
            this.pixelChangeByCoords[coords].push(change);
        }

        this.canvas.overlayCanvas(xCoords, yCoords, colours);
        this.confirmation.show();
        this.saveChangesDebounce();
    }

    private isButton(event: MouseEvent, ...button: number[]): boolean {
        return event.button === undefined || button.indexOf(event.button) >= 0;
    }

    private serialiseCoords(x: number, y: number): string {
        return `${x}-${y}`;
    }

    private notifyInfo(message: string): void {
        this.onInfoListeners.forEach((listener) => setTimeout(() => listener(message), 700));
    }

    private notifySuccess(message: string): void {
        this.onSuccessListeners.forEach((listener) => setTimeout(() => listener(message), 700));
    }

    private notifyError(message: string): void {
        this.onErrorListeners.forEach((listener) => setTimeout(() => listener(message), 700));
    }

    private saveChanges(): void {
        if (this.pixelUndoHistory.length === 0) {
            return;
        }

        localStorage.setItem(this.changesKey(), JSON.stringify(this.pixelUndoHistory));
    }

    private loadChanges(): void {
        try {
            const changes = localStorage.getItem(this.changesKey());

            if (changes) {
                const decoded = JSON.parse(changes);

                for (const change of decoded) {
                    this.draw(change);
                }

                this.notifyInfo("Unsaved changes restored!");
            }
        } catch (e) {
            this.removeChanges();
        }
    }

    private removeChanges(): void {
        localStorage.removeItem(this.changesKey());
    }

    private changesKey(): string {
        return `${DrawHandler.DRAW_CHANGES_KEY}:${this.canvas.getProperties().canvasId}`;
    }
}
